import { useState, useEffect } from "react";
import { Comment } from "@/components/video/comments/types";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

export const useComments = (videoId: string) => {
  const [comments, setComments] = useState<Comment[]>([]);  // Initialize with empty array
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchComments = async () => {
      const { data, error } = await supabase
        .from('comments')
        .select(`
          *,
          profiles:user_id (
            name,
            username,
            profile_image_url
          )
        `)
        .eq('video_id', videoId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching comments:', error);
        return;
      }

      const formattedComments: Comment[] = data.map(formatCommentData);
      setComments(formattedComments);
    };

    fetchComments();

    // Subscribe to new comments and profile updates
    const commentsChannel = supabase
      .channel('comments-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `video_id=eq.${videoId}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const { data: newCommentData, error } = await supabase
              .from('comments')
              .select(`
                *,
                profiles:user_id (
                  name,
                  username,
                  profile_image_url
                )
              `)
              .eq('id', payload.new.id)
              .single();

            if (error || !newCommentData) {
              console.error('Error fetching new comment:', error);
              return;
            }

            const formattedComment = formatCommentData(newCommentData);
            setComments(prev => [formattedComment, ...prev]);
          }
        }
      )
      .subscribe();

    // Subscribe to profile updates
    const profilesChannel = supabase
      .channel('profiles-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          // Refetch comments to get updated profile information
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(commentsChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, [videoId, queryClient]);

  return { comments };
};

const formatCommentData = (comment: any): Comment => ({
  id: comment.id,
  user: {
    name: comment.profiles.name || 'Anonymous',
    avatar: comment.profiles.profile_image_url || '',
    username: comment.profiles.username || 'anonymous'
  },
  text: comment.text,
  timestamp: new Date(comment.created_at).toLocaleDateString(),
  likes: comment.likes || 0
});